/**
 * Hero canvas stars — Poisson-distributed starfield with intro CTA morph
 */

class HeroCanvasStars {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('.hero-canvas-stars__stage');
    this.media = root.querySelector('[data-hero-media]');
    this.parallaxLayer = root.querySelector('[data-hero-parallax]');
    this.canvas = root.querySelector('[data-hero-canvas]');
    this.scrollCta = root.querySelector('[data-hero-scroll-cta]');
    this.copy =
      root.querySelector('[data-hero-copy]') ||
      root.querySelector('.hero-canvas-stars__content-inner');
    this.ctx = this.canvas?.getContext('2d', { alpha: true });

    this.width = 0;
    this.height = 0;
    this.stageWidth = 0;
    this.stageHeight = 0;
    this.dpr = 1;
    this.stars = [];
    this.morphStarIndex = 0;
    this.time = 0;
    this.rafId = null;
    this.isVisible = true;
    this.isPaused = false;
    this.pointerX = 0;
    this.pointerY = 0;
    this.smoothPointerX = 0;
    this.smoothPointerY = 0;
    this.hasPointer = false;
    this.pointerPending = false;
    this.pendingPointerEvent = null;
    this.pointerRafScheduled = false;
    this.morphProgress = 0;
    this.morphIntroStart = null;
    this.morphIntroComplete = false;
    this.morphStartCaptured = false;
    this.morphStartX = 0;
    this.morphStartY = 0;
    this.morphStartRadius = 0;
    this.morphStartOpacity = 1;
    this.ctaHovered = false;
    this.ctaActive = false;
    this.ctaX = 0;
    this.ctaY = 0;
    this.ctaRadius = 0;
    this.ctaBackgroundProgress = 0;
    this.ctaBackgroundStart = null;
    this.starRgb = { r: 255, g: 255, b: 255 };
    this.primaryRgb = { r: 211, g: 90, b: 28 };
    this.primaryDarkRgb = { r: 168, g: 68, b: 21 };

    this.onResize = this.onResize.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onScrollCtaPointerUp = this.onScrollCtaPointerUp.bind(this);
    this.onScrollCtaKeyDown = this.onScrollCtaKeyDown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.tick = this.tick.bind(this);
    this.scrollParallaxRaf = null;

    this.readConfig();
    this.setupReducedMotion();
  }

  readConfig() {
    const { dataset } = this.root;

    this.config = {
      starQuantity: Number(dataset.starQuantity) || 120,
      starSizeMin: Number(dataset.starSizeMin) || 0.5,
      starSizeMax: Number(dataset.starSizeMax) || 2.5,
      starSpacingMin: Number(dataset.starSpacingMin) || 24,
      floatSpeed: Number(dataset.floatSpeed) || 0.0004,
      parallaxStrength: Number(dataset.parallaxStrength) || 0.025,
      scrollParallax: Number(dataset.scrollParallax) || 0,
      mouseInfluence: Number(dataset.mouseInfluence) || 0.06,
      glowStrength: Number(dataset.glowStrength) || 1,
      animationSpeed: Number(dataset.animationSpeed) || 1,
      arrowSize: Number(dataset.arrowSize) || 10,
      ctaIntroDelay: Number(dataset.ctaIntroDelay) || 400,
      ctaIntroDuration: Number(dataset.ctaIntroDuration) || 2400,
      ctaGlow: Number(dataset.ctaGlow) || 1,
      ctaSize: Number(dataset.ctaSize) || 56,
      disableAnimation: dataset.disableAnimation === 'true',
      pauseOffScreen: dataset.pauseOffScreen !== 'false',
      reducedMotionOverride: dataset.reducedMotionOverride === 'true',
    };

    this.starRgb = this.parseColor(dataset.starColor || '#ffffff');
    this.readPrimaryColors();
  }

  readPrimaryColors() {
    const styles = getComputedStyle(this.root);
    this.primaryRgb = this.parseColor(
      styles.getPropertyValue('--color-solaris-ember').trim() || '#C75217'
    );
    this.primaryDarkRgb = this.parseColor(
      styles.getPropertyValue('--color-solaris-ember').trim() || '#C75217'
    );
  }

  setupReducedMotion() {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.reducedMotion =
      this.config.reducedMotionOverride ||
      this.prefersReducedMotion ||
      this.config.disableAnimation;

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onReducedMotionChange = (event) => {
      if (!this.config.reducedMotionOverride) {
        this.reducedMotion = event.matches || this.config.disableAnimation;
        if (this.reducedMotion) {
          this.morphProgress = 1;
          this.morphIntroComplete = true;
        }
        this.setupScrollParallax();
        this.updateScrollCtaVisibility();
      }
    };
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
  }

  init() {
    if (!this.stage || !this.media || !this.canvas || !this.ctx) return;

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.root);
    this.resizeObserver.observe(this.stage);

    if (this.reducedMotion) {
      this.morphProgress = 1;
      this.morphIntroComplete = true;
    }

    if (this.config.pauseOffScreen) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isVisible = entry?.isIntersecting ?? true;
          if (this.isVisible && !this.isPaused) {
            this.start();
          } else if (!this.isVisible) {
            this.stop();
          }
        },
        { root: null, threshold: 0 }
      );
      this.intersectionObserver.observe(this.root);
    }

    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    this.scrollCta?.addEventListener('pointerup', this.onScrollCtaPointerUp);
    this.scrollCta?.addEventListener('keydown', this.onScrollCtaKeyDown);

    this.setupScrollParallax();
    this.onResize();
    this.updateScrollCtaVisibility();
    this.start();
  }

  destroy() {
    this.stop();
    this.teardownScrollParallax();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.reducedMotionQuery?.removeEventListener('change', this.onReducedMotionChange);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    this.scrollCta?.removeEventListener('pointerup', this.onScrollCtaPointerUp);
    this.scrollCta?.removeEventListener('keydown', this.onScrollCtaKeyDown);
  }

  setupScrollParallax() {
    const layer = this.parallaxLayer;
    if (!layer && !this.copy) return;

    this.teardownScrollParallax();

    if (this.reducedMotion) {
      layer?.classList.remove('hero-canvas-stars__parallax--enabled');
      layer?.style.setProperty('--hero-bg-shift', '0px');
      this.resetCopyFade();
      return;
    }

    // Viewport-fixed bg: image stays ~100vh and shifts with scroll (no scale).
    if (layer) {
      layer.classList.add('hero-canvas-stars__parallax--enabled');
    }
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.updateScrollParallax();
  }

  teardownScrollParallax() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.scrollParallaxRaf) {
      cancelAnimationFrame(this.scrollParallaxRaf);
      this.scrollParallaxRaf = null;
    }

    const layer = this.parallaxLayer;
    if (layer) {
      layer.classList.remove('hero-canvas-stars__parallax--enabled');
      layer.style.removeProperty('--hero-bg-shift');
      layer.style.removeProperty('--hero-bg-position-y');
      layer.style.removeProperty('--hero-scroll-parallax-offset');
      layer.style.removeProperty('--hero-scroll-parallax-scale');
    }
    this.resetCopyFade();
  }

  onScroll() {
    if (this.scrollParallaxRaf) return;

    this.scrollParallaxRaf = requestAnimationFrame(() => {
      this.scrollParallaxRaf = null;
      this.updateScrollParallax();
    });
  }

  getVisibleImageFocalY(layer) {
    const images = layer.querySelectorAll('.hero-canvas-stars__image');
    for (const img of images) {
      if (getComputedStyle(img).display === 'none') continue;
      const focalY = getComputedStyle(img).getPropertyValue('--hero-focal-y').trim();
      if (!focalY) continue;
      const parsed = parseFloat(focalY);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 50;
  }

  resetCopyFade() {
    if (!this.copy) return;
    this.copy.style.removeProperty('--hero-copy-opacity');
  }

  updateCopyFade() {
    if (
      !this.copy ||
      this.reducedMotion ||
      window.matchMedia('(max-width: 989px)').matches
    ) {
      this.resetCopyFade();
      return;
    }

    const rootRect = this.root.getBoundingClientRect();

    if (rootRect.bottom <= 0 || rootRect.top >= window.innerHeight) {
      this.copy.style.setProperty('--hero-copy-opacity', '0');
      return;
    }

    // Fade from how far the section has scrolled up — guarantees full
    // opacity at the rest position and a clean restore on scroll-back.
    const scrolled = Math.max(0, -rootRect.top);
    const fadeDistance = Math.max(96, Math.min(200, window.innerHeight * 0.22));
    const opacity = 1 - Math.min(1, scrolled / fadeDistance);
    this.copy.style.setProperty('--hero-copy-opacity', opacity.toFixed(3));
  }

  updateScrollParallax() {
    const layer = this.parallaxLayer;
    if (!this.reducedMotion && layer) {
      const rootRect = this.root.getBoundingClientRect();
      const viewH = window.innerHeight || 1;
      const mediaH = rootRect.height || 1;
      const maxShift = Math.max(0, mediaH - viewH);

      // Always pin so the viewport-tall image covers what's on screen —
      // never leave a navy gap at the bottom of the section.
      const shift = Math.min(Math.max(0, -rootRect.top), maxShift);
      layer.style.setProperty('--hero-bg-shift', `${Math.round(shift)}px`);

      // Scroll parallax setting = subtle crop drift (not translate lag).
      // 0 = locked framing; higher = slow vertical drift within the image.
      // Drift centers on the image focal point so merchant framing is preserved.
      const drift = Math.min(Math.max(this.config.scrollParallax || 0, 0), 0.35);
      const progress = maxShift > 0 ? shift / maxShift : 0;
      const baseY = this.getVisibleImageFocalY(layer);
      const positionY = baseY - drift * 14 + progress * drift * 28;
      layer.style.setProperty('--hero-bg-position-y', `${positionY.toFixed(2)}%`);
    }

    this.updateCopyFade();
  }

  getCtaMetrics() {
    const radius = this.config.ctaSize * 0.5;
    const bottomInset = this.config.ctaSize * 0.35;
    const stageWidth = this.stageWidth || this.width;
    const stageHeight = this.stageHeight || this.height;

    return {
      x: stageWidth * 0.5,
      y: stageHeight - radius - bottomInset,
      radius,
    };
  }

  captureMorphStart(star) {
    if (this.morphStartCaptured || !star) return;

    this.morphStartX = star.baseX + star.offsetX + star.currentOffsetX;
    this.morphStartY = star.baseY + star.offsetY + star.currentOffsetY;
    this.morphStartRadius = star.baseRadius;
    this.morphStartOpacity = star.opacity;
    this.morphStartCaptured = true;
  }

  lerp(start, end, t) {
    return start + (end - start) * t;
  }

  parseColor(hex) {
    const value = hex.replace('#', '');
    const normalized =
      value.length === 3
        ? value
            .split('')
            .map((char) => char + char)
            .join('')
        : value;

    return {
      r: parseInt(normalized.slice(0, 2), 16) || 255,
      g: parseInt(normalized.slice(2, 4), 16) || 255,
      b: parseInt(normalized.slice(4, 6), 16) || 255,
    };
  }

  onResize() {
    const rootRect = this.root.getBoundingClientRect();
    const stageRect = this.stage.getBoundingClientRect();
    if (rootRect.width === 0 || rootRect.height === 0) return;
    if (stageRect.width === 0 || stageRect.height === 0) return;

    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Canvas / stars fill the full component (stage + optional below content)
    this.width = rootRect.width;
    this.height = rootRect.height;
    // Arrow stays anchored to the stage bottom
    this.stageWidth = stageRect.width;
    this.stageHeight = stageRect.height;

    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.readPrimaryColors();
    this.generateStars();

    if (this.ctaActive) {
      this.positionScrollCta();
    }

    this.updateScrollParallax();
  }

  generateStars() {
    const area = this.width * this.height;
    const idealSpacing = Math.sqrt(area / (this.config.starQuantity * 0.85));
    const minDist = Math.max(this.config.starSpacingMin, idealSpacing);

    const points = this.poissonDiscSample(
      this.width,
      this.height,
      minDist,
      this.config.starQuantity
    );

    this.stars = points.map((point, index) => {
      const seed = (index + 1) * 97.31;
      const depth = 0.2 + this.seededRandom(seed) * 0.8;
      const t = this.seededRandom(seed + 1);
      const radius =
        this.config.starSizeMin +
        t * (this.config.starSizeMax - this.config.starSizeMin);

      return {
        x: point.x,
        y: point.y,
        baseX: point.x,
        baseY: point.y,
        radius,
        baseRadius: radius,
        opacity: 0.25 + this.seededRandom(seed + 2) * 0.45,
        depth,
        velocity: 0.4 + this.seededRandom(seed + 3) * 0.9,
        seed,
        wanderRadius: 14 + this.seededRandom(seed + 4) * 32,
        phaseX: this.seededRandom(seed + 5) * Math.PI * 2,
        phaseY: this.seededRandom(seed + 6) * Math.PI * 2,
        speedMult: 0.45 + this.seededRandom(seed + 7) * 0.9,
        offsetX: 0,
        offsetY: 0,
        currentOffsetX: 0,
        currentOffsetY: 0,
        targetOffsetX: 0,
        targetOffsetY: 0,
        repelX: 0,
        repelY: 0,
      };
    });

    this.morphStarIndex = this.stars.length > 0 ? Math.floor(Math.random() * this.stars.length) : 0;

    if (!this.morphIntroComplete) {
      this.morphStartCaptured = false;
    }
  }

  poissonDiscSample(width, height, minDist, maxPoints) {
    const cellSize = minDist / Math.SQRT2;
    const cols = Math.ceil(width / cellSize);
    const rows = Math.ceil(height / cellSize);
    const grid = new Array(cols * rows);
    const points = [];
    const active = [];
    const margin = minDist * 0.35;

    const toIndex = (x, y) => {
      const col = Math.floor(x / cellSize);
      const row = Math.floor(y / cellSize);
      return col + row * cols;
    };

    const inBounds = (x, y) =>
      x >= margin && x < width - margin && y >= margin && y < height - margin;

    const isFarEnough = (x, y) => {
      const col = Math.floor(x / cellSize);
      const row = Math.floor(y / cellSize);

      for (let i = -2; i <= 2; i += 1) {
        for (let j = -2; j <= 2; j += 1) {
          const c = col + i;
          const r = row + j;
          if (c < 0 || r < 0 || c >= cols || r >= rows) continue;

          const neighbor = grid[c + r * cols];
          if (!neighbor) continue;

          const dx = neighbor.x - x;
          const dy = neighbor.y - y;
          if (dx * dx + dy * dy < minDist * minDist) return false;
        }
      }

      return true;
    };

    const addPoint = (x, y) => {
      const point = { x, y };
      points.push(point);
      active.push(point);
      grid[toIndex(x, y)] = point;
      return point;
    };

    const seedCount = Math.min(12, Math.max(4, Math.ceil(maxPoints / 25)));

    for (let i = 0; i < seedCount; i += 1) {
      const x = margin + Math.random() * (width - margin * 2);
      const y = margin + Math.random() * (height - margin * 2);
      if (isFarEnough(x, y)) {
        addPoint(x, y);
      }
    }

    if (points.length === 0) {
      addPoint(width * 0.5, height * 0.5);
    }

    while (active.length > 0 && points.length < maxPoints * 4) {
      const activeIndex = Math.floor(Math.random() * active.length);
      const point = active[activeIndex];
      let found = false;

      for (let attempt = 0; attempt < 30; attempt += 1) {
        const angle = Math.random() * Math.PI * 2;
        const distance = minDist + Math.random() * minDist;
        const x = point.x + Math.cos(angle) * distance;
        const y = point.y + Math.sin(angle) * distance;

        if (!inBounds(x, y) || !isFarEnough(x, y)) continue;

        addPoint(x, y);
        found = true;
        break;
      }

      if (!found) {
        active.splice(activeIndex, 1);
      }
    }

    for (let i = points.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const swap = points[i];
      points[i] = points[j];
      points[j] = swap;
    }

    if (points.length > maxPoints) {
      return points.slice(0, maxPoints);
    }

    return points;
  }

  seededRandom(seed) {
    const value = Math.sin(seed * 12.9898) * 43758.5453;
    return value - Math.floor(value);
  }

  isPointerInStage(clientX, clientY) {
    if (!this.stage) return false;
    const rect = this.stage.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }

  isInteractiveTarget(target) {
    if (target.closest('[data-hero-scroll-cta]')) return false;

    return Boolean(
      target.closest(
        '.cta-block, .button, a[href], button, input, textarea, select, [role="button"]'
      )
    );
  }

  isPointerInCta(clientX, clientY, padding = 1) {
    if (!this.ctaActive) return false;

    const rect = this.stage.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const dx = x - this.ctaX;
    const dy = y - this.ctaY;
    const hitRadius = this.ctaRadius * padding;

    return dx * dx + dy * dy <= hitRadius * hitRadius;
  }

  onPointerMove(event) {
    if (!this.isPointerInStage(event.clientX, event.clientY)) {
      if (this.hasPointer) {
        this.onPointerLeave();
      }
      return;
    }

    this.pendingPointerEvent = event;
    if (this.pointerRafScheduled) return;

    this.pointerRafScheduled = true;
    requestAnimationFrame(() => {
      this.pointerRafScheduled = false;
      const pointerEvent = this.pendingPointerEvent;
      this.pendingPointerEvent = null;
      if (!pointerEvent || !this.stage) return;

      const rect = this.root.getBoundingClientRect();
      this.pointerX = pointerEvent.clientX - rect.left;
      this.pointerY = pointerEvent.clientY - rect.top;
      this.hasPointer = true;
      this.pointerPending = true;
    });
  }

  onPointerLeave() {
    this.hasPointer = false;
    this.pointerPending = true;
    this.ctaHovered = false;
    this.root.classList.remove('hero-canvas-stars--cta-visible');
  }

  onPointerDown(event) {
    if (event.pointerType === 'touch') return;
    if (!this.isPointerInStage(event.clientX, event.clientY)) return;
    if (event.target.closest('[data-hero-scroll-cta]')) return;
    if (this.isInteractiveTarget(event.target)) return;
    if (!this.ctaActive) return;
    if (!this.isPointerInCta(event.clientX, event.clientY, 1.15)) return;

    event.preventDefault();
    this.scrollToNextSection();
  }

  onPointerUp(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!this.isPointerInStage(event.clientX, event.clientY)) return;
    if (event.target.closest('[data-hero-scroll-cta]')) return;
    if (this.isInteractiveTarget(event.target)) return;
    if (!this.ctaActive) return;
    if (!this.isPointerInCta(event.clientX, event.clientY, 1.25)) return;

    event.preventDefault();
    this.scrollToNextSection();
  }

  updateMorphProgress(timestamp) {
    if (this.morphIntroComplete) return;

    if (this.reducedMotion) {
      this.morphProgress = 1;
      this.morphIntroComplete = true;
      this.updateScrollCtaVisibility();
      return;
    }

    if (this.morphIntroStart === null) {
      this.morphIntroStart = timestamp;
    }

    const elapsed = timestamp - this.morphIntroStart;

    if (elapsed < this.config.ctaIntroDelay) {
      this.morphProgress = 0;
      return;
    }

    const animElapsed = elapsed - this.config.ctaIntroDelay;
    const rawProgress = animElapsed / this.config.ctaIntroDuration;

    if (rawProgress >= 1) {
      this.morphProgress = 1;
      this.morphIntroComplete = true;
    } else {
      this.morphProgress = rawProgress;
    }

    this.updateScrollCtaVisibility();
  }

  updateCtaBackgroundProgress(timestamp) {
    if (!this.morphIntroComplete) {
      this.ctaBackgroundProgress = 0;
      this.ctaBackgroundStart = null;
      this.root.classList.remove('hero-canvas-stars--cta-primary');
      return;
    }

    if (this.reducedMotion) {
      this.ctaBackgroundProgress = 1;
      this.root.classList.add('hero-canvas-stars--cta-primary');
      return;
    }

    if (this.ctaBackgroundStart === null) {
      this.ctaBackgroundStart = timestamp;
    }

    const elapsed = timestamp - this.ctaBackgroundStart;
    const duration = 700;
    this.ctaBackgroundProgress = Math.min(1, elapsed / duration);

    this.root.classList.toggle(
      'hero-canvas-stars--cta-primary',
      this.ctaBackgroundProgress >= 1
    );
  }

  updateScrollCtaVisibility() {
    if (!this.scrollCta) return;

    if (this.morphIntroComplete) {
      this.scrollCta.hidden = false;
      this.positionScrollCta();
    } else {
      this.scrollCta.hidden = true;
    }
  }

  positionScrollCta() {
    if (!this.scrollCta) return;

    const size = Math.max(this.ctaRadius * 2, 44);

    this.scrollCta.style.left = `${this.ctaX}px`;
    this.scrollCta.style.top = `${this.ctaY}px`;
    this.scrollCta.style.width = `${size}px`;
    this.scrollCta.style.height = `${size}px`;
  }

  onScrollCtaPointerUp(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    this.scrollToNextSection();
  }

  onScrollCtaKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.scrollToNextSection();
    }
  }

  readCssPx(value) {
    const parsed = Number.parseFloat(String(value || '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Space to leave under the sticky header once the scroll settles.
   * Uses compact header height (not the tall transparent --header-offset).
   */
  getScrollPaddingTop() {
    const header = document.querySelector('.site-header');
    const gap = 8;

    // Mobile scroll-hide: header is off-screen, no padding needed.
    if (header?.classList.contains('site-header--hidden')) {
      return 0;
    }

    const sticky = this.readCssPx(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--header-sticky-offset'
      )
    );
    if (sticky > 0) return sticky + gap;

    if (header) {
      return Math.ceil(header.getBoundingClientRect().height) + gap;
    }

    return (
      this.readCssPx(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-offset'
        )
      ) + gap
    );
  }

  scrollToNextSection() {
    const now = Date.now();
    if (now - (this.lastScrollAt || 0) < 400) return;
    this.lastScrollAt = now;

    const section = this.root.closest('.shopify-section');
    const below = this.root.querySelector('[data-hero-below]');
    const nextSection = section?.nextElementSibling;
    const behavior = this.reducedMotion ? 'auto' : 'smooth';
    const target = below || nextSection;
    // Nudge past the target top so content sits further into view.
    const extraScroll = 114;

    if (target) {
      const padding = this.getScrollPaddingTop();
      const top = Math.max(
        0,
        target.getBoundingClientRect().top + window.scrollY - padding + extraScroll
      );
      window.scrollTo({ top, behavior });
      return;
    }

    window.scrollBy({
      top: Math.max(0, window.innerHeight - this.getScrollPaddingTop() + extraScroll),
      behavior,
    });
  }

  start() {
    if (this.rafId !== null || this.isPaused) return;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  tick(timestamp) {
    if (!this.isVisible && this.config.pauseOffScreen) {
      this.rafId = null;
      return;
    }

    const delta = this.lastTimestamp ? timestamp - this.lastTimestamp : 16;
    this.lastTimestamp = timestamp;
    this.time += delta * this.config.animationSpeed;

    this.updateMorphProgress(timestamp);
    this.updateCtaBackgroundProgress(timestamp);

    if (this.pointerPending) {
      this.pointerPending = false;
      this.updatePointerTargets();
    }

    this.render();
    this.rafId = requestAnimationFrame(this.tick);
  }

  updatePointerTargets() {
    const centerX = this.width * 0.5;
    const centerY = this.height * 0.5;
    const targetX = this.hasPointer ? this.pointerX : centerX;
    const targetY = this.hasPointer ? this.pointerY : centerY;
    const influence = this.config.mouseInfluence;

    this.smoothPointerX += (targetX - this.smoothPointerX) * influence;
    this.smoothPointerY += (targetY - this.smoothPointerY) * influence;
  }

  render() {
    const { ctx, width, height } = this;
    ctx.clearRect(0, 0, width, height);

    const centerX = width * 0.5;
    const centerY = height * 0.5;
    const pointerDeltaX = this.smoothPointerX - centerX;
    const pointerDeltaY = this.smoothPointerY - centerY;
    const cta = this.getCtaMetrics();
    const morphStar = this.stars[this.morphStarIndex];
    const easedMorph = this.easeInOutCubic(this.morphProgress);
    const isMorphing = easedMorph > 0 && easedMorph < 1 && !this.reducedMotion;

    if (isMorphing && morphStar) {
      this.captureMorphStart(morphStar);
    }

    const morphDrawX = isMorphing
      ? this.lerp(this.morphStartX, cta.x, easedMorph)
      : cta.x;
    const morphDrawY = isMorphing
      ? this.lerp(this.morphStartY, cta.y, easedMorph)
      : cta.y;
    const morphDrawRadius = isMorphing
      ? this.lerp(this.morphStartRadius, cta.radius, easedMorph)
      : cta.radius;

    this.ctaX = cta.x;
    this.ctaY = cta.y;
    this.ctaRadius = cta.radius;
    this.ctaActive = this.morphIntroComplete;

    if (this.ctaActive && this.hasPointer) {
      const dx = this.pointerX - cta.x;
      const dy = this.pointerY - cta.y;
      const hoverRadius = cta.radius * 1.15;
      this.ctaHovered = dx * dx + dy * dy <= hoverRadius * hoverRadius;
      this.root.classList.toggle('hero-canvas-stars--cta-visible', this.ctaHovered);
    } else {
      this.ctaHovered = false;
      this.root.classList.remove('hero-canvas-stars--cta-visible');
    }

    for (let i = 0; i < this.stars.length; i += 1) {
      const star = this.stars[i];
      const isMorph = i === this.morphStarIndex;

      star.repelX = 0;
      star.repelY = 0;

      if (easedMorph > 0 && !isMorph) {
        const dx = star.baseX - cta.x;
        const dy = star.baseY - cta.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const repelRadius = this.config.ctaSize * 2.4;

        if (dist < repelRadius) {
          const force = (1 - dist / repelRadius) * easedMorph * 28;
          star.repelX = (dx / dist) * force;
          star.repelY = (dy / dist) * force;
        }
      }

      if (!this.reducedMotion && !(isMorph && isMorphing)) {
        const time = this.time * this.config.floatSpeed * star.speedMult;
        const primaryX = Math.sin(time + star.phaseX) * star.wanderRadius;
        const primaryY = Math.cos(time * 0.82 + star.phaseY) * star.wanderRadius;
        const secondaryX =
          Math.sin(time * 1.41 + star.phaseX * 1.7) * star.wanderRadius * 0.38;
        const secondaryY =
          Math.cos(time * 1.18 + star.phaseY * 2.1) * star.wanderRadius * 0.32;

        star.offsetX = (primaryX + secondaryX) * star.depth;
        star.offsetY = (primaryY + secondaryY) * star.depth;

        star.targetOffsetX = pointerDeltaX * this.config.parallaxStrength * star.depth;
        star.targetOffsetY = pointerDeltaY * this.config.parallaxStrength * star.depth;

        const influence = this.config.mouseInfluence;
        star.currentOffsetX += (star.targetOffsetX - star.currentOffsetX) * influence;
        star.currentOffsetY += (star.targetOffsetY - star.currentOffsetY) * influence;
      } else if (isMorph && isMorphing) {
        star.offsetX = 0;
        star.offsetY = 0;
        star.currentOffsetX = 0;
        star.currentOffsetY = 0;
      } else if (this.reducedMotion) {
        star.offsetX = 0;
        star.offsetY = 0;
        star.currentOffsetX = 0;
        star.currentOffsetY = 0;
      }

      if (isMorph && isMorphing) {
        const starFade = 1 - easedMorph;
        if (starFade > 0.02) {
          this.drawStar(
            morphDrawX,
            morphDrawY,
            morphDrawRadius,
            this.morphStartOpacity * starFade,
            starFade
          );
        }
        this.drawCTA(morphDrawX, morphDrawY, morphDrawRadius, easedMorph, 0);
        continue;
      }

      const drawX =
        star.baseX + star.offsetX + star.currentOffsetX + star.repelX;
      const drawY =
        star.baseY + star.offsetY + star.currentOffsetY + star.repelY;

      if (isMorph && this.morphIntroComplete) {
        continue;
      }

      if (isMorph && this.reducedMotion && this.morphProgress >= 1) {
        continue;
      }

      this.drawStar(drawX, drawY, star.radius, star.opacity);
    }

    if (this.morphIntroComplete || (this.reducedMotion && this.morphProgress >= 1)) {
      this.drawCTA(cta.x, cta.y, cta.radius, 1, this.ctaBackgroundProgress);
    }

    if (this.ctaActive) {
      this.positionScrollCta();
    }
  }

  drawStar(x, y, radius, opacity, glowScale = 1) {
    const { ctx, starRgb, config } = this;
    const glowRadius = radius * (1.5 + config.glowStrength * 0.65) * glowScale;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, glowRadius);

    gradient.addColorStop(0, `rgba(${starRgb.r},${starRgb.g},${starRgb.b},${opacity})`);
    gradient.addColorStop(1, `rgba(${starRgb.r},${starRgb.g},${starRgb.b},0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
    ctx.fill();
  }

  lerpColor(from, to, t) {
    return {
      r: Math.round(this.lerp(from.r, to.r, t)),
      g: Math.round(this.lerp(from.g, to.g, t)),
      b: Math.round(this.lerp(from.b, to.b, t)),
    };
  }

  drawCTA(x, y, radius, morphBlend = 1, backgroundProgress = 0) {
    const { ctx, config, ctaHovered, primaryRgb, primaryDarkRgb } = this;
    const defaultRadius = this.config.ctaSize * 0.5;
    const baseRadius = radius ?? defaultRadius;
    const hoverScale = ctaHovered && morphBlend >= 1 ? 1.08 : 1;
    const drawRadius = baseRadius * hoverScale;
    const sizeScale = drawRadius / defaultRadius;
    const blend = Math.max(0, Math.min(1, morphBlend));
    const glow = config.ctaGlow * (ctaHovered && morphBlend >= 1 ? 1.25 : 1) * blend;
    const bgBlend =
      blend >= 0.98 ? this.easeInOutCubic(Math.max(0, Math.min(1, backgroundProgress))) : 0;
    const fillRgb = this.lerpColor(
      { r: 255, g: 255, b: 255 },
      ctaHovered && bgBlend >= 1 ? primaryDarkRgb : primaryRgb,
      bgBlend
    );
    const outerRadius = drawRadius * 2.2;
    const outerWhiteStrength = (1 - bgBlend) * glow;

    if (outerWhiteStrength > 0.01) {
      const outerGradient = ctx.createRadialGradient(x, y, drawRadius * 0.2, x, y, outerRadius);
      outerGradient.addColorStop(0, `rgba(255,255,255,${0.55 * outerWhiteStrength})`);
      outerGradient.addColorStop(0.45, `rgba(255,255,255,${0.18 * outerWhiteStrength})`);
      outerGradient.addColorStop(1, 'rgba(255,255,255,0)');

      ctx.fillStyle = outerGradient;
      ctx.beginPath();
      ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    if (bgBlend < 1) {
      const innerGradient = ctx.createRadialGradient(x, y, 0, x, y, drawRadius);
      innerGradient.addColorStop(0, `rgba(255,255,255,${0.28 * glow})`);
      innerGradient.addColorStop(0.7, `rgba(255,255,255,${0.12 * glow})`);
      innerGradient.addColorStop(1, `rgba(255,255,255,${0.04 * glow})`);

      ctx.fillStyle = innerGradient;
      ctx.beginPath();
      ctx.arc(x, y, drawRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    if (bgBlend > 0) {
      const fillAlpha = this.lerp(0.35, 1, bgBlend);
      ctx.fillStyle = `rgba(${fillRgb.r},${fillRgb.g},${fillRgb.b},${fillAlpha})`;
      ctx.beginPath();
      ctx.arc(x, y, drawRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    const chevronBlend = Math.max(0, Math.min(1, (blend - 0.55) / 0.45));
    if (chevronBlend > 0) {
      const chevronAlpha = bgBlend >= 1 ? 1 : 0.9 * glow * chevronBlend;
      ctx.strokeStyle = `rgba(255,255,255,${chevronAlpha})`;
      ctx.lineWidth = 1.75 * sizeScale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const chevron = config.arrowSize * sizeScale * chevronBlend;
      ctx.beginPath();
      ctx.moveTo(x - chevron * 0.55, y - chevron * 0.2);
      ctx.lineTo(x, y + chevron * 0.45);
      ctx.lineTo(x + chevron * 0.55, y - chevron * 0.2);
      ctx.stroke();
    }
  }

  easeInOutCubic(t) {
    if (t < 0.5) return 4 * t * t * t;
    return 1 - (-2 * t + 2) ** 3 / 2;
  }
}

class HeroCanvasStarsComponent extends HTMLElement {
  connectedCallback() {
    this._instance?.destroy();
    this._instance = new HeroCanvasStars(this);
    this._instance.init();
  }

  disconnectedCallback() {
    this._instance?.destroy();
    this._instance = null;
  }
}

function initHeroCanvasStars(scope = document) {
  scope.querySelectorAll('hero-canvas-stars-component').forEach((element) => {
    if (!(element instanceof HeroCanvasStarsComponent)) {
      customElements.upgrade(element);
    }
  });
}

if (!customElements.get('hero-canvas-stars-component')) {
  customElements.define('hero-canvas-stars-component', HeroCanvasStarsComponent);
}

document.addEventListener('DOMContentLoaded', () => initHeroCanvasStars());

document.addEventListener('shopify:section:load', (event) => {
  initHeroCanvasStars(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('hero-canvas-stars-component').forEach((element) => {
    element._instance?.destroy();
    element._instance = null;
  });
});
